import { FC, ReactElement } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { TFunction } from "i18next";
import {
  PackInstance,
  PackStatus,
} from "../../redux/actions/licenses/licenceTypes";

export const packStatusColor = (status: PackStatus): string => {
  switch (status) {
    case PackStatus.active:
      return "#2e7d32";
    case PackStatus.bound:
      return "#1565c0";
    case PackStatus.emptied:
      return "#ed6c02";
    case PackStatus.expired:
      return "#d32f2f";
    default:
      return "#9e9e9e";
  }
};

export const packStatusLabel = (status: PackStatus): string => {
  switch (status) {
    case PackStatus.active:
      return "Active";
    case PackStatus.bound:
      return "Bound";
    case PackStatus.emptied:
      return "Emptied";
    case PackStatus.expired:
      return "Expired";
    default:
      return "Unbound";
  }
};

export const packStatusDesc = (status: PackStatus, t: TFunction): string => {
  switch (status) {
    case PackStatus.active:
      return t("licenses.pack_status_active_desc");
    case PackStatus.bound:
      return t("licenses.pack_status_bound_desc");
    case PackStatus.emptied:
      return t("licenses.pack_status_emptied_desc");
    case PackStatus.expired:
      return t("licenses.pack_status_expired_desc");
    default:
      return t("licenses.pack_status_unbound_desc");
  }
};

export const PacksTooltipContent: FC<{
  packs: PackInstance[];
  t: TFunction;
  language: string;
}> = ({ packs, t, language }) => (
  <Box sx={{ p: 1, minWidth: 300 }}>
    <Typography
      variant="caption"
      sx={{ fontWeight: 700, display: "block", mb: 0.75, fontSize: "0.72rem" }}
    >
      {t("licenses.Packs")} ({packs.length})
    </Typography>
    <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
      {packs.map((pack, idx) => (
        <Box
          key={pack.id}
          sx={{
            py: 0.5,
            borderBottom:
              idx < packs.length - 1 ? "1px solid rgba(0,0,0,0.07)" : "none",
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}
          >
            <Tooltip
              title={packStatusDesc(pack.statusConstantId, t)}
              placement="top"
              arrow
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  flexShrink: 0,
                  cursor: "help",
                  bgcolor: packStatusColor(pack.statusConstantId),
                }}
              />
            </Tooltip>
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 700,
                color: packStatusColor(pack.statusConstantId),
              }}
            >
              {packStatusLabel(pack.statusConstantId)}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 600,
                ml: "auto",
                color: "text.primary",
              }}
            >
              {pack.initialCredits.toLocaleString()} cr.
            </Typography>
          </Box>
          <Typography
            sx={{ fontSize: "0.65rem", color: "text.secondary", pl: 2 }}
          >
            <Box component="span" sx={{ fontWeight: 600 }}>
              {t("licenses.activation")}:
            </Box>{" "}
            {new Date(pack.activationDate).toLocaleDateString(language)}
            {"  ·  "}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {t("licenses.expiration")}:
            </Box>{" "}
            {new Date(pack.expiryDate).toLocaleDateString(language)}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

const tooltipSx = {
  tooltip: {
    sx: {
      bgcolor: "background.paper",
      color: "text.primary",
      boxShadow: 4,
      p: 0,
      maxWidth: 420,
    },
  },
  arrow: { sx: { color: "background.paper" } },
};

export const PacksTooltip: FC<{
  packs: PackInstance[];
  t: TFunction;
  language: string;
  children: ReactElement;
  placement?: "bottom-start" | "top" | "top-start" | "bottom" | "right";
}> = ({ packs, t, language, children, placement = "bottom-start" }) => {
  if (!packs.length) return children;
  return (
  <Tooltip
    title={<PacksTooltipContent packs={packs} t={t} language={language} />}
    placement={placement}
    arrow
    slotProps={tooltipSx}
  >
    {children}
  </Tooltip>
  );
};
